/**
 * Scroll utilities for managing body scroll locking
 *
 * iOS Safari/Chrome has special behavior where overflow: hidden doesn't prevent scrolling.
 * This implementation uses a combination of techniques to work around this limitation.
 */

export const MODAL_OPEN_CLASS = 'modal-open';
export const SIDEBAR_OPEN_CLASS = 'sidebar-open';

// Store original body styles to restore them later
let originalBodyStyles = null;
let scrollY = 0;

// Check if device is iOS (including various iOS browsers)
const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) ||
    /CriOS|FxiOS|OPiOS|mercury/.test(navigator.userAgent);
};

// Safe closest helper that works even when target is not an Element or closest is unavailable
const safeClosest = (node, selector) => {
  if (!node) return null;
  // If event target is not an Element (e.g., Text, Window), move up to parent element
  let el = node.nodeType === 1 ? node : (node.parentElement || node.parentNode || null);
  const matches = (elem, sel) => (
    elem.matches?.(sel) || elem.webkitMatchesSelector?.(sel) || elem.msMatchesSelector?.(sel)
  );
  while (el && el !== document) {
    if (el.nodeType === 1 && matches(el, selector)) return el;
    el = el.parentElement || el.parentNode || null;
  }
  return null;
};

/**
 * Disables body scrolling by setting overflow: hidden and using iOS-specific fixes
 * @param {string} identifier - CSS class to add to body for tracking
 */
export const disableBodyScroll = (identifier) => {
  // Prevent multiple calls from interfering with each other
  if (document.body.classList.contains(identifier)) {
    return;
  }

  document.body.classList.add(identifier);

  // Store original styles if not already stored
  if (!originalBodyStyles) {
    originalBodyStyles = {
      overflow: document.body.style.overflow,
      overflowY: document.body.style.overflowY,
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      right: document.body.style.right,
      width: document.body.style.width,
      height: document.body.style.height
    };
  }

  // Get current scroll position
  scrollY = window.scrollY;

  // Apply scroll lock styles
  document.body.style.overflowY = 'hidden';
  document.documentElement.style.overflowY = 'hidden';

  // iOS-specific handling
  if (isIOS()) {
    // Use position fixed approach for iOS to prevent scrolling
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.style.height = '100%';
    
    // Prevent touch events on body but allow them in modals
    document.body.style.touchAction = 'none';
    document.body.style.webkitOverflowScrolling = 'auto';
    
    // Add a more sophisticated event handler that allows modal scrolling
    const preventScroll = (e) => {
      const target = e.target || document.activeElement || null;
      
      // Check if the target is within a modal
      const withinOverlay = safeClosest(target, '.modal-overlay');
      const withinContent = safeClosest(target, '.modal__content');
      const withinModal = safeClosest(target, '.modal');
      
      // If the event is within a modal, allow it
      if (withinOverlay || withinContent || withinModal) {
        return true;
      }
      
      // For touch events, be more permissive if they're not on the body
      if (e.type === 'touchmove' && target && target !== document.body && safeClosest(target, 'body')) {
        return true;
      }
      
      // Prevent the scroll/touch event
      try {
        e.preventDefault();
        e.stopPropagation();
      } catch (_) {
        // noop - some synthetic events may not be cancelable
      }
      return false;
    };

    // Add both scroll and touchmove listeners with smart targeting
    window.addEventListener('scroll', preventScroll, { passive: false });
    window.addEventListener('touchmove', preventScroll, { passive: false });

    // Store the event listeners for cleanup
    window._scrollLockListeners = window._scrollLockListeners || [];
    window._scrollLockListeners.push(preventScroll);
  }
};

/**
 * Enables body scrolling by restoring original styles and scroll position
 * @param {string} identifier - CSS class to remove from body
 */
export const enableBodyScroll = (identifier) => {
  document.body.classList.remove(identifier);

  if (!document.body.classList.contains(MODAL_OPEN_CLASS) && !document.body.classList.contains(SIDEBAR_OPEN_CLASS)) {
    // Restore original styles
    if (originalBodyStyles) {
      document.body.style.overflow = originalBodyStyles.overflow;
      document.body.style.overflowY = originalBodyStyles.overflowY;
      document.body.style.position = originalBodyStyles.position;
      document.body.style.top = originalBodyStyles.top;
      document.body.style.left = originalBodyStyles.left;
      document.body.style.right = originalBodyStyles.right;
      document.body.style.width = originalBodyStyles.width;
      document.body.style.height = originalBodyStyles.height;
      document.body.style.touchAction = '';
      document.body.style.webkitOverflowScrolling = '';

      // Reset documentElement styles
      document.documentElement.style.overflowY = 'auto';

      // iOS-specific cleanup
      if (isIOS()) {
        // Remove event listeners
        if (window._scrollLockListeners) {
          window._scrollLockListeners.forEach(listener => {
            window.removeEventListener('scroll', listener);
            window.removeEventListener('touchmove', listener);
          });
          window._scrollLockListeners = [];
        }

        // Restore scroll position
        if (scrollY > 0) {
          requestAnimationFrame(() => {
            window.scrollTo(0, scrollY);
          });
        }
      }

      // Clear stored styles
      originalBodyStyles = null;
      scrollY = 0;
    } else {
      // Fallback to simple restoration
      document.body.style.overflowY = 'auto';
      document.documentElement.style.overflowY = 'auto';
    }
  }
};
